import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { refitPenPathShapeBounds } from '@/lib/elements/pen-path-utils'
import type { TextShapeOutline } from '@/lib/text/types/text-shape-outline'

/**
 * App-wide default shape fill (matches `shape.fill || '#e5e7eb'` in `shape-renderer/components/shape-body-path.tsx`) — used
 * only to replace an EXPLICITLY invisible fill (transparent/none/empty), never an unset one, since
 * the renderer already falls back to this same color for `undefined`.
 */
const CONVERTED_SHAPE_DEFAULT_FILL = '#e5e7eb'

/**
 * App-wide default shape stroke (matches `shape.stroke || '#000000'` / `shape.strokeWidth || 1`
 * in `shape-renderer/components/shape-body-path.tsx`) — used only to replace an EXPLICITLY invisible stroke, for the same
 * reason as `CONVERTED_SHAPE_DEFAULT_FILL` above. Every text-frame creation path stamps
 * `stroke: 'transparent'`, `strokeWidth: 0` (not `undefined`) so the frame doesn't render as a box
 * while just holding text — and unlike an unset stroke, that explicit value survives the
 * `...shape` spread below untouched and is NOT caught by the renderer's own `|| '#000000'`
 * fallback (which only fires on falsy, and `'transparent'` is truthy).
 */
const CONVERTED_SHAPE_DEFAULT_STROKE = '#000000'
const CONVERTED_SHAPE_DEFAULT_STROKE_WIDTH = 1

function isExplicitlyInvisibleColor(color: string | undefined): boolean {
  if (color == null) return false
  const v = color.trim().toLowerCase()

  return v === '' || v === 'transparent' || v === 'none'
}

/**
 * Converts a text frame's `ShapeData` into the closed compound-path shape that replaces it —
 * `type: 'path'` with `path`/`pathCommands` set to the extracted glyph outline (see
 * `buildTextShapeOutline`), and its `x`/`y`/`width`/`height` tightened to hug that outline's real
 * ink via `refitPenPathShapeBounds` (the same tight-bounds machinery `pages/home`'s node-edit drag
 * commit already uses — see `pen-path-utils.ts`).
 *
 * `kindId: 2` (Irregular Polygon) is set explicitly since the outline is always closed; `save`
 * would infer the same value anyway (`resolveKindId` in `stowed-element-builder.ts`).
 *
 * Clears `paragraphs` — the result is no longer a text frame, matching the acceptance criteria's
 * "Type/typography controls become disabled for that object" (`isShapeTextFrame` keys off
 * `paragraphs` presence).
 *
 * Known limitation (flagged, not fixed here): there is no colorId→CSS resolution for text-run
 * fill/stroke anywhere in this codebase (see `text-to-shape-outline.ts`'s doc comment), so the
 * original text's real color can't be carried over yet. An explicitly invisible original fill or
 * stroke (transparent/none/empty — the case for every text-frame creation path, since a text
 * frame is deliberately paint-less so it doesn't render as a box while just holding text) is
 * replaced with the app's own default shape fill/stroke, so the shape looks like any other
 * freshly-drawn shape — and matches what it reloads as (`checkpoint-to-shapes.ts` falls back to
 * the same plain-shape default once the shape no longer carries `paragraphs`) — instead of
 * rendering with a visible fill but no border until the next reload. Any other fill/stroke is
 * carried over unchanged.
 */
export function buildShapeFromTextOutline(shape: ShapeData, outline: TextShapeOutline): ShapeData {
  const strokeWasInvisible = isExplicitlyInvisibleColor(shape.stroke)

  const pathShape = {
    ...shape,
    type: 'path' as const,
    kindId: 2,
    path: outline.path,
    pathCommands: undefined,
    paragraphs: undefined,
    fill: isExplicitlyInvisibleColor(shape.fill) ? CONVERTED_SHAPE_DEFAULT_FILL : shape.fill,
    stroke: strokeWasInvisible ? CONVERTED_SHAPE_DEFAULT_STROKE : shape.stroke,
    strokeWidth: strokeWasInvisible ? CONVERTED_SHAPE_DEFAULT_STROKE_WIDTH : shape.strokeWidth,
  }

  return refitPenPathShapeBounds(pathShape)
}
